import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { getMenuItems, MenuItem } from './utils/menuItems';
import SidebarLink from './SidebarLink';
import SidebarSection from './SidebarSection';
import { useAuth } from '../../contexts/AuthContext';

type SidebarProps = {
  isSidebarOpen: boolean;
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const Sidebar: React.FC<SidebarProps> = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const location = useLocation();
  const { courseId } = useParams<{ courseId: string }>();
  const { user } = useAuth();
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [showNewUserIndicator, setShowNewUserIndicator] = useState(false);
  const [isAvatarDropdownOpen, setIsAvatarDropdownOpen] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const firstLoad = React.useRef(true);

  // Detectar se é nova aluna e controlar indicador
  useEffect(() => {
    const hasVisitedComece = localStorage.getItem('visited_comece_section');
    const isNewUser = !hasVisitedComece;
    setShowNewUserIndicator(isNewUser);
  }, []);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isAvatarDropdownOpen && !target.closest('.avatar-dropdown-container')) {
        setIsAvatarDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAvatarDropdownOpen]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );

    if (section === 'comece' && showNewUserIndicator) {
      setShowNewUserIndicator(false);
      localStorage.setItem('visited_comece_section', 'true');
    }
  };

  
  const handleReadCategory = (path: string) => {
      setMenuItems(prev =>
        prev.map(item =>
          item.type === "section" && item.id === "debates"
            ? {
                ...item,
                subItems: item.subItems.map(sub =>
                  sub.path === path ? { ...sub, unreadCount: 0 } : sub
                ),
              }
            : item
        )
      );
    };

  const isActive = (path: string) => {
    if (path === '/aluna' && location.pathname === '/aluna') return true;
    if (path !== '/aluna' && location.pathname.startsWith(path)) return true;
    return false;
  };


  useEffect(() => {
    if (!courseId) return;

    const fetchMenu = async () => {
      try {
        if (firstLoad.current) {
          setLoadingMenu(true);
        }
        
        const items = await getMenuItems(expandedSections, courseId, user);
        setMenuItems(items);
      } catch (err) {
        console.error("Erro ao carregar menu:", err);
      } finally {
        if (firstLoad.current) {
          setLoadingMenu(false);
          firstLoad.current = false;
        }
      }
    };

    fetchMenu();
  }, [courseId, expandedSections, user]);

  return (
    
      <aside
        className={`fixed left-0 top-16 bottom-0 bg-white dark:bg-gray-800 shadow-lg z-30 transition-transform duration-300 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 w-72`}
      >
        <nav className="p-4 h-full overflow-y-auto">
          {loadingMenu ? (
            <div className="space-y-4 animate-pulse">
              <div className="h-[48px] bg-gray-200 dark:bg-gray-600 rounded w-full"></div>
              <div className="ml-[30px] h-[40px] bg-gray-200 dark:bg-gray-600 rounded w-1/1"></div>
              <div className="ml-[30px] h-[40px] bg-gray-200 dark:bg-gray-600 rounded w-1/1"></div>
              <div className="ml-[30px] h-[40px] bg-gray-200 dark:bg-gray-600 rounded w-1/1"></div>
              <div className="ml-[30px] h-[40px] bg-gray-200 dark:bg-gray-600 rounded w-1/1"></div>
              <div className="ml-[30px] h-[40px] bg-gray-200 dark:bg-gray-600 rounded w-1/1"></div>
            </div>
          ) : (
            <ul className="space-y-2">
              {menuItems.map((item: any, index: number) => {
                if (item.type === 'link') {
                  return (
                    <SidebarLink
                      key={index}
                      to={item.path}
                      label={item.label}
                      icon={item.icon}
                      onClick={() => setIsSidebarOpen(false)}
                    />
                  );
                } else if (item.type === 'section') {
                  return (
                    <SidebarSection
                      key={index}
                      id={item.id}
                      label={item.label}
                      icon={item.icon}
                      isExpanded={item.isExpanded}
                      showNewUserIndicator={showNewUserIndicator}
                      onToggle={toggleSection}
                      onLinkClick={() => setIsSidebarOpen(false)}
                      subItems={item.subItems}
                      isActivePath={isActive}
                      disabled={item.disabled}
                      onReadCategory={handleReadCategory}
                    />
                  );
                }
                return null;
              })}
            </ul>
          )}
        </nav>
      </aside>
  );
};

export default Sidebar;